import { createApp } from "vue";
import App from "./App.vue";
import router from "@/router";
import axios from 'axios';
import Swal from 'sweetalert2';

// Configurar Axios para usar base URL do Laravel (via .env: VITE_APP_URL)
axios.defaults.baseURL = import.meta.env.VITE_APP_URL || '';

// Enviar cookies (sessão + CSRF) automaticamente em todas as requisições
axios.defaults.withCredentials = true;

// ============================================
// Interceptor global de erros HTTP
// ============================================
axios.interceptors.response.use(
  // Sucesso: retorna a resposta normalmente
  (response) => response,

  // Erro: trata de acordo com o status
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';

    // Ignora erros de autenticação em rotas específicas (handled pelos componentes)
    const isAuthRoute = url.includes('/api/user') ||
                        url.includes('/api/login') ||
                        url.includes('/sanctum/csrf-cookie');

    if (isAuthRoute && (status === 401 || status === 419)) {
      return Promise.reject(error);
    }

    switch (status) {
      case 401:
        // Não autenticado - redireciona para login (evita múltiplos modais)
        if (!window._sessaoExpiradaExibida) {
          window._sessaoExpiradaExibida = true;
          localStorage.removeItem('isAuthenticated');
          Swal.fire({
            icon: 'warning',
            title: 'Sessão expirada',
            text: 'Sua sessão expirou. Faça login novamente.',
            confirmButtonText: 'Ir para Login',
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window._sessaoExpiradaExibida = false;
            window.location.href = '/login';
          });
        }
        break;

      case 419:
        // CSRF token expirado - recarrega a página para obter novo token
        Swal.fire({
          icon: 'warning',
          title: 'Token expirado',
          text: 'Sua página expirou. Recarregue para continuar.',
          confirmButtonText: 'Recarregar',
          allowOutsideClick: false,
        }).then(() => {
          window.location.reload();
        });
        break;

      case 403:
        // Sem permissão
        Swal.fire({
          icon: 'error',
          title: 'Acesso negado',
          text: 'Você não tem permissão para realizar esta ação.',
          confirmButtonColor: '#dc3545'
        });
        break;

      case 404:
        // Recurso não encontrado (silencioso para algumas rotas)
        if (!url.includes('/api/user')) {
          Swal.fire({
            icon: 'error',
            title: 'Não encontrado',
            text: 'O recurso solicitado não foi encontrado.',
            confirmButtonColor: '#dc3545'
          });
        }
        break;

      case 422:
        // Erro de validação - deixa o componente tratar
        break;

      case 429:
        // Rate limit excedido
        Swal.fire({
          icon: 'warning',
          title: 'Muitas requisições',
          text: 'Aguarde um momento antes de tentar novamente.',
          confirmButtonColor: '#ffc107'
        });
        break;

      case 500:
      case 502:
      case 503:
        // Erro do servidor
        Swal.fire({
          icon: 'error',
          title: 'Erro no servidor',
          text: 'Ocorreu um erro interno. Tente novamente em alguns instantes.',
          confirmButtonColor: '#dc3545'
        });
        break;

      default:
        // Erro de rede ou desconhecido
        if (!error.response) {
          Swal.fire({
            icon: 'error',
            title: 'Erro de conexão',
            text: 'Não foi possível conectar ao servidor. Verifique sua internet.',
            confirmButtonColor: '#dc3545'
          });
        }
    }

    // Marca o erro como já tratado pelo interceptor (evita duplicação de Swal nos componentes)
    error._handled = true;

    return Promise.reject(error);
  }
);

const app = createApp(App);
app.use(router);

// ============================================
// Handler global para erros Vue não-capturados
// Apenas loga no console sem mostrar popup
// ============================================
app.config.errorHandler = (err, instance, info) => {
  console.error('[Vue Error]', err, info);
};

// ============================================
// Handler global para Promises rejeitadas sem catch
// Apenas loga no console sem mostrar popup
// ============================================
window.addEventListener('unhandledrejection', (event) => {
  console.error('[Unhandled Promise]', event.reason);
});

app.mount("#app");
